/**
 * @fileoverview Search popup — the full-viewport overlay behind the header's search icon.
 *
 * Markup lives in sections/search-popup.liquid. This element owns open/close, the
 * scroll lock, the focus trap and the predictive-search lookup; it never writes
 * chrome. Only the contents of [data-search-rows] and the visibility of the groups
 * change, so the merchant's headings always survive a query.
 *
 * @example
 * // Any control in the page opens it — no wiring needed beyond the attribute.
 * <a href="/search" data-search-toggle>…</a>
 *
 * @example
 * // Programmatic control via window events, matching the drawer and modal.
 * window.dispatchEvent(new Event('open-search-popup'));
 * window.dispatchEvent(new Event('close-search-popup'));
 *
 * @note Content is in the light DOM rather than a shadow root, so the panel is
 *   styleable from assets/search-popup.css with the theme's scheme tokens. The
 *   focus handling below is the same shape as component-drawer.js, which is the
 *   best-built of the theme's overlay components — it is copied rather than
 *   imported because that file's backdrop and slide-in panel are baked into its
 *   shadow styles and are not what this design asks for.
 */

/**
 * Elements that can hold focus. `:not([tabindex="-1"])` keeps programmatic-only
 * targets out of the Tab cycle while still allowing .focus() on them.
 */
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button",
  "input",
  "select",
  "textarea",
  "details > summary",
  "iframe",
  "[contenteditable]",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/** Long enough that a fast typist sends one request, short enough to feel live. */
const DEBOUNCE_MS = 250;

/**
 * The featured groups give way as soon as there is anything to search for. One
 * character is enough because predictive search prefix-matches as you type — it is
 * a different engine from storefront search, not a substring filter over it.
 */
const MIN_QUERY_LENGTH = 1;

class SearchPopup extends HTMLElement {
  /** @type {HTMLInputElement|null} */
  #input = null;
  /** @type {HTMLElement|null} */
  #inner = null;
  /** @type {HTMLElement|null} */
  #resultsTarget = null;
  /** @type {HTMLElement|null} */
  #empty = null;
  /** @type {HTMLElement|null} */
  #status = null;
  /** @type {Record<string, HTMLElement>} The row lists, keyed by resource name */
  #lists = {};
  /** @type {Record<string, string>} Server-rendered featured rows, kept for the reset */
  #featured = {};
  /** Where focus was when the popup opened, so it can be handed back on close */
  #returnFocusTo = null;
  /** @type {AbortController|null} */
  #controller = null;
  #debounceId = 0;
  #boundOpen = null;
  #boundClose = null;
  #boundKeydown = null;
  #boundInput = null;
  #boundSubmit = null;
  #boundClick = null;

  constructor() {
    super();

    // Bound once, so re-registering a listener is a no-op rather than a duplicate.
    this.#boundOpen = () => this.open();
    this.#boundClose = () => this.close();
    this.#boundInput = () => this.#onInput();
    this.#boundSubmit = (event) => event.preventDefault();
    this.#boundClick = (event) => this.#onClick(event);
    this.#boundKeydown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key === "Tab") this.#trapTab(event);
    };
  }

  connectedCallback() {
    // The theme editor re-renders the section while the previous instance is
    // already parked on <body>. Drop the stale one rather than opening two.
    for (const other of document.querySelectorAll("search-popup")) {
      if (other !== this) other.remove();
    }

    // Move to <body> so a transformed ancestor can never become the containing
    // block for `position: fixed`, and so the overlay clears the sticky header.
    // This re-enters connectedCallback with <body> as the parent.
    if (this.parentElement && this.parentElement !== document.body) {
      document.body.appendChild(this);
      return;
    }

    this.#input = this.querySelector("[data-search-input]");
    this.#inner = this.querySelector("[data-search-panel]");
    this.#resultsTarget = this.querySelector("[data-search-results-target]");
    this.#empty = this.querySelector("[data-search-empty]");
    this.#status = this.querySelector("[data-search-status]");

    for (const list of this.querySelectorAll("[data-search-rows]")) {
      const name = list.dataset.searchRows;
      this.#lists[name] = list;
      // Cached now, while the markup is still the merchant's curated pickers.
      this.#featured[name] = list.innerHTML;
    }
    this.#syncGroups();

    this.#input?.addEventListener("input", this.#boundInput);
    this.querySelector("[data-search-form]")?.addEventListener("submit", this.#boundSubmit);
    this.querySelector("[data-search-close]")?.addEventListener("click", this.#boundClose);
    this.addEventListener("click", this.#boundClick);

    window.addEventListener("open-search-popup", this.#boundOpen);
    window.addEventListener("close-search-popup", this.#boundClose);
    window.addEventListener("popstate", this.#boundClose);
  }

  disconnectedCallback() {
    window.removeEventListener("open-search-popup", this.#boundOpen);
    window.removeEventListener("close-search-popup", this.#boundClose);
    window.removeEventListener("popstate", this.#boundClose);
    document.removeEventListener("keydown", this.#boundKeydown);
    clearTimeout(this.#debounceId);
    this.#controller?.abort();
    // Removed mid-open — a theme-editor re-render — must not leave the page frozen.
    if (this.isOpen) this.#unlockScroll();
  }

  /** @returns {boolean} Whether the popup is open */
  get isOpen() {
    return this.hasAttribute("open");
  }

  /** Opens the popup and moves focus into the field */
  open() {
    if (this.isOpen) return;

    // Captured before focus moves into the panel, so close() can hand it back.
    this.#returnFocusTo =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    this.setAttribute("open", "");
    this.setAttribute("role", "dialog");
    this.setAttribute("aria-modal", "true");
    this.#lockScroll();
    document.addEventListener("keydown", this.#boundKeydown);

    // After the frame that made the panel visible — a hidden element cannot take focus.
    requestAnimationFrame(() => {
      this.#input?.focus();
      this.#input?.select();
    });

    this.dispatchEvent(new CustomEvent("search:open", { bubbles: true }));
  }

  /** Closes the popup and resets it to the featured state */
  close() {
    if (!this.isOpen) return;

    this.removeAttribute("open");
    // A closed overlay must stop advertising itself as an open modal dialog.
    this.removeAttribute("role");
    this.removeAttribute("aria-modal");
    document.removeEventListener("keydown", this.#boundKeydown);
    this.#unlockScroll();

    // Reset on the way out rather than on the way in, so the next open is clean
    // without flashing the previous query's results through the fade.
    clearTimeout(this.#debounceId);
    this.#controller?.abort();
    if (this.#input) this.#input.value = "";
    this.#restoreFeatured();

    // The trigger may be gone by now (a re-rendered section), hence the check.
    const returnTo = this.#returnFocusTo;
    this.#returnFocusTo = null;
    if (returnTo?.isConnected) returnTo.focus();

    this.dispatchEvent(new CustomEvent("search:close", { bubbles: true }));
  }

  // ── Scroll lock ───────────────────────────────────────────

  /**
   * Freezes the page behind the overlay.
   *
   * On <html>, not <body>. The viewport takes its overflow from the root element
   * and only falls through to <body> when the root's own overflow is `visible` —
   * and normalize.css sets `overflow-x: clip` on both. So locking <body>, which is
   * what component-drawer.js does, has no effect in this theme.
   *
   * Removing the scrollbar frees its width and the page behind would jump; the
   * padding puts that width back. `position: fixed` resolves against the viewport,
   * not the root's content box, so the overlay itself is unaffected by it.
   */
  #lockScroll() {
    const root = document.documentElement;
    const scrollbar = window.innerWidth - root.clientWidth;
    if (scrollbar > 0) root.style.paddingRight = `${scrollbar}px`;
    root.style.overflow = "hidden";
  }

  /** Releases it, unless a drawer or modal is still holding the page. */
  #unlockScroll() {
    if (document.querySelector("drawer-element[open], modal-element[open]")) return;
    const root = document.documentElement;
    root.style.overflow = "";
    root.style.paddingRight = "";
  }

  // ── Query ─────────────────────────────────────────────────

  #onInput() {
    const term = this.#input.value.trim();
    clearTimeout(this.#debounceId);

    if (term.length < MIN_QUERY_LENGTH) {
      this.#controller?.abort();
      this.#restoreFeatured();
      return;
    }

    this.#debounceId = setTimeout(() => this.#search(term), DEBOUNCE_MS);
  }

  /**
   * Renders sections/search-popup-results.liquid through the Predictive Search
   * endpoint — the documented way to run predictive search from a theme, and the
   * surface the Shopify Search & Discovery app configures. Matching and ranking
   * belong to it; only the four parameters below are ours.
   *
   * Two of them are deliberately absent, because setting them would take the
   * decision away from the merchant's app settings:
   * - resources[options][unavailable_products] — S&D owns how sold-out products
   *   are placed (default `last`).
   * - resources[options][fields] — leave it to the default set
   *   (title, product_type, variants.title, vendor).
   *
   * resources[type] is pinned rather than inherited, and that IS an override of
   * the app's result-type setting. It has to be: the endpoint default is
   * `query,product,collection,page`, and this popup shows products only,
   * and Shop and Articles are the only two groups with anywhere to put a result.
   * Albums are a metaobject, which predictive search has no resource type for.
   *
   * @param {string} term
   */
  async #search(term) {
    // Cancel the in-flight lookup so a slower earlier response cannot land last.
    this.#controller?.abort();
    this.#controller = new AbortController();

    const url = new URL(this.dataset.searchUrl, window.location.origin);
    url.searchParams.set("q", term);
    url.searchParams.set("resources[type]", "product");
    url.searchParams.set("resources[limit]", this.dataset.searchLimit || "5");
    url.searchParams.set("resources[limit_scope]", "each");
    url.searchParams.set("section_id", this.dataset.searchSection);

    try {
      const response = await fetch(url, { signal: this.#controller.signal });
      if (!response.ok) throw new Error(`Predictive search responded ${response.status}`);
      this.#render(await response.text());
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error(error);
      // A failed lookup should not strand the panel on another query's results.
      this.#restoreFeatured();
    }
  }

  /** @param {string} html The rendered section */
  #render(html) {
    const parsed = new DOMParser().parseFromString(html, "text/html");
    let total = 0;

    for (const [name, list] of Object.entries(this.#lists)) {
      // Curated-only groups have no counterpart in the response — leave their rows
      // alone so they are still there when the field is cleared.
      if (this.#isFeaturedOnly(list)) continue;

      const incoming = parsed.querySelector(`[data-search-rows="${name}"]`);
      list.innerHTML = incoming ? incoming.innerHTML : "";
      total += list.childElementCount;
    }
    this.#syncGroups(true);
    // A new query starts at the top, not wherever the last one was left scrolled.
    // The panel is the scroll container, not the results — see search-popup.css.
    if (this.#inner) this.#inner.scrollTop = 0;

    // The message is rendered server-side so its copy and the searched terms both
    // come from Liquid rather than being reassembled here.
    const empty = parsed.querySelector("[data-search-empty]");
    if (this.#empty) {
      this.#empty.textContent = empty ? empty.textContent.trim() : "";
      this.#empty.hidden = total > 0;
    }

    this.#announce(total);
  }

  #restoreFeatured() {
    for (const [name, list] of Object.entries(this.#lists)) {
      list.innerHTML = this.#featured[name] ?? "";
    }
    this.#syncGroups();
    if (this.#inner) this.#inner.scrollTop = 0;

    if (this.#empty) this.#empty.hidden = true;
    if (this.#status) this.#status.textContent = "";
  }

  /**
   * Hides any group whose list came back empty, so no heading stands alone, and —
   * while a query is running — the curated-only groups as well.
   * @param {boolean} querying Whether the panel is showing results rather than the
   *   featured state.
   */
  #syncGroups(querying = false) {
    for (const list of Object.values(this.#lists)) {
      const group = list.closest("[data-search-group]");
      if (!group) continue;
      group.hidden =
        (querying && this.#isFeaturedOnly(list)) || list.childElementCount === 0;
    }
  }

  /**
   * @param {HTMLElement} list
   * @returns {boolean} Whether the list belongs to a group that only ever shows
   *   curated rows — albums, which predictive search has no resource type for.
   */
  #isFeaturedOnly(list) {
    return Boolean(list.closest("[data-search-group]")?.hasAttribute("data-search-featured-only"));
  }

  /** @param {number} count */
  #announce(count) {
    if (!this.#status) return;
    const template = this.dataset.i18nResultsCount;
    this.#status.textContent = template
      ? template.replace("%s", String(count))
      : String(count);
  }

  // ── Interaction ───────────────────────────────────────────

  /**
   * Closes on a click that landed on the overlay itself rather than on any of its
   * content — the panel's own empty space, in other words. Tested by identity so a
   * click on a group's padding never counts as a dismissal.
   * @param {MouseEvent} event
   */
  #onClick(event) {
    const { target } = event;
    if (target === this || target === this.#inner || target === this.#resultsTarget) {
      this.close();
    }
  }

  // ── Focus management ──────────────────────────────────────

  /** @returns {HTMLElement[]} Tabbable descendants, in document order */
  #focusable() {
    return [...this.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
      (el) => !el.disabled && el.getClientRects().length > 0,
    );
  }

  /**
   * Keeps Tab inside the panel. Without this, tabbing past the last row walks into
   * the page behind the overlay, which is hidden from view and unreachable by mouse.
   * @param {KeyboardEvent} event
   */
  #trapTab(event) {
    const focusable = this.#focusable();
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    // Focus outside the panel (a script moved it) — pull it back in.
    if (!this.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get("search-popup")) {
  customElements.define("search-popup", SearchPopup);

  // Delegated, so a trigger only has to carry the attribute — no per-section wiring.
  //
  // The trigger is a <button>, not a link to /search: the overlay is the only
  // search surface this theme has, and /search renders an empty stub. The tradeoff
  // is that search is now unreachable without JavaScript, and that a missing
  // <search-popup> makes the icon inert rather than falling back to a page.
  document.addEventListener("click", (event) => {
    const toggle = event.target instanceof Element && event.target.closest("[data-search-toggle]");
    if (!toggle) return;

    const popup = document.querySelector("search-popup");
    if (!popup) {
      console.warn("[search-popup] No <search-popup> in the document — is the Search Popup section in the header group?");
      return;
    }

    event.preventDefault();
    if (popup.isOpen) popup.close();
    else popup.open();
  });
}
